import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * hallmark critique · PrivacyPanel
 * tokens locked from docs/design.md §1 (pebble hairline · pure-white surface)
 *                                    §2 (23px/700 title, 18px/29 body)
 *                                    §7 (SIMULATED pill on mocked surfaces)
 * states covered: idle · loading (aggregator not yet returned) · loaded ·
 *                 empty (no calls in window) · error (mirrored from hook)
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * Right-column panel that REPLACES `BunchingRail` when the Demand tab is
 * active. Structure mirrors the operator ergonomics of BunchingRail:
 *   1. Header block — "Demand · consented + aggregated" + SIMULATED pill.
 *   2. Copy paragraph — verbatim from the runbook.
 *   3. DemandSimulateButton — the operator's demo trigger.
 *   4. Stat block — total calls · unique H3 cells · top-3 stops.
 *
 * The clay accent for the viewport stays owned by the heatmap layer + the
 * Demand nav pill in the header — this panel is pure white + pebble
 * hairlines + deep-ink text.
 */
import { useMemo } from "react";
import { SimulatedPill } from "../common/SimulatedPill";
import { DemandSimulateButton } from "./DemandSimulateButton";
import { AS_4_STOPS } from "../../lib/tokens";
const railStyle = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "var(--color-pure-white)",
    borderLeft: "1px solid var(--color-pebble)",
};
const headerStyle = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "18px 20px 12px 20px",
    borderBottom: "1px solid var(--color-pebble)",
};
const eyebrowStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
    marginBottom: 4,
};
const titleStyle = {
    fontSize: 23,
    lineHeight: 1,
    fontWeight: 700,
    letterSpacing: "-0.23px",
    color: "var(--color-deep-ink)",
    margin: 0,
};
const scrollStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    padding: 20,
    overflowY: "auto",
    flex: "1 1 auto",
};
const bodyStyle = {
    fontSize: 18,
    fontWeight: 400,
    lineHeight: "29px",
    color: "var(--color-cool-ash)",
    margin: 0,
};
const statBlockStyle = {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: 16,
    paddingTop: 16,
    borderTop: "1px solid var(--color-pebble)",
};
const statLabelStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "var(--color-cool-ash)",
};
const statValueStyle = {
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1,
    color: "var(--color-deep-ink)",
    marginTop: 4,
    letterSpacing: "-0.01em",
};
const listStyle = {
    padding: 0,
    margin: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: 6,
};
const listItemStyle = {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    fontSize: 15,
    color: "var(--color-deep-ink)",
    fontWeight: 700,
};
function computeStats(cells) {
    const totalCalls = cells.reduce((acc, c) => acc + c.count, 0);
    const uniqueCells = cells.filter((c) => c.h3_cell !== "unknown").length;
    // Union stops across cells with per-cell frequency weight. We don't have
    // per-stop counts from the aggregator (backend cell payload is a set of
    // distinct stop_ids), so we approximate frequency by counting how many
    // cells surface each stop_id weighted by cell count.
    const stopWeight = new Map();
    for (const c of cells) {
        for (const s of c.stops ?? []) {
            stopWeight.set(s, (stopWeight.get(s) ?? 0) + Math.max(1, c.count));
        }
    }
    const stopLabelById = new Map(AS_4_STOPS.map((s) => [s.stopId, s.name]));
    const topStops = [...stopWeight.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([stopId, count]) => ({
        stopId,
        count,
        label: stopLabelById.get(stopId) ?? stopId,
    }));
    return { totalCalls, uniqueCells, topStops };
}
function formatWindow(sinceMinutes, generatedAt) {
    const gen = generatedAt ? new Date(generatedAt) : null;
    const clock = gen && !Number.isNaN(gen.getTime())
        ? gen.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : "—";
    return `Last ${sinceMinutes} min · updated ${clock}`;
}
export function PrivacyPanel({ cells, status, error, generatedAt, sinceMinutes, onSimulate, onSimulated, forcedButtonState, }) {
    const stats = useMemo(() => computeStats(cells), [cells]);
    const windowCaption = useMemo(() => formatWindow(sinceMinutes, generatedAt), [sinceMinutes, generatedAt]);
    const emptyState = status === "loaded" && cells.length === 0;
    const isError = status === "error";
    return (_jsxs("aside", { style: railStyle, "data-testid": "privacy-panel", "aria-label": "Demand consent and privacy", children: [_jsxs("div", { style: headerStyle, children: [_jsxs("div", { style: { minWidth: 0 }, children: [_jsx("div", { style: eyebrowStyle, children: "Privacy pane" }), _jsx("h2", { style: titleStyle, children: "Demand \u00B7 consented + aggregated" })] }), _jsx(SimulatedPill, { testId: "privacy-simulated-pill", style: { flex: "0 0 auto" } })] }), _jsxs("div", { style: scrollStyle, children: [_jsx("p", { style: bodyStyle, "data-testid": "privacy-panel-copy", children: "Every buses_near_stop call from a consented AI assistant becomes an anonymous demand pulse \u2014 H3-bucketed, hashed consent token, no PII stored. This is the sensor network our operator sees." }), _jsx(DemandSimulateButton, { onSimulated: async (res) => {
                            if (onSimulated)
                                await onSimulated(res);
                            if (onSimulate)
                                await onSimulate();
                        }, forcedState: forcedButtonState }), _jsxs("div", { style: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            padding: "10px 12px",
                            border: "1px solid var(--color-pebble)",
                            borderRadius: 0,
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: status === "loading"
                                ? "var(--color-cool-ash)"
                                : isError
                                    ? "var(--color-clay-ember)"
                                    : "var(--color-cool-ash)",
                        }, "data-testid": "privacy-status", "data-status": status, children: [_jsx("span", { children: windowCaption }), _jsx("span", { children: status === "loading"
                                    ? "syncing"
                                    : isError
                                        ? "error"
                                        : emptyState
                                            ? "no calls yet"
                                            : `${stats.totalCalls} calls` })] }), isError && error && (_jsx("div", { style: {
                            fontSize: 14,
                            lineHeight: 1.4,
                            color: "var(--color-deep-ink)",
                            padding: 12,
                            border: "1px solid var(--color-pebble)",
                            backgroundColor: "var(--color-pure-white)",
                        }, "data-testid": "privacy-error", children: error })), _jsxs("section", { style: statBlockStyle, "data-testid": "privacy-stat-block", "aria-label": "Aggregated demand statistics", children: [_jsxs("div", { children: [_jsx("div", { style: statLabelStyle, children: "Total calls" }), _jsx("div", { style: statValueStyle, "data-testid": "stat-total-calls", children: stats.totalCalls })] }), _jsxs("div", { children: [_jsx("div", { style: statLabelStyle, children: "Unique H3 cells" }), _jsx("div", { style: statValueStyle, "data-testid": "stat-unique-cells", children: stats.uniqueCells })] })] }), _jsxs("section", { style: {
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            paddingTop: 16,
                            borderTop: "1px solid var(--color-pebble)",
                        }, "data-testid": "privacy-top-stops", "aria-label": "Top-3 stops asked about", children: [_jsx("div", { style: statLabelStyle, children: "Top 3 stops" }), stats.topStops.length === 0 ? (_jsx("div", { style: { fontSize: 14, color: "var(--color-cool-ash)" }, "data-testid": "privacy-top-stops-empty", children: "No consented calls yet \u2014 press Simulate to seed the aggregator." })) : (_jsx("ol", { style: listStyle, children: stats.topStops.map((s, i) => (_jsxs("li", { style: listItemStyle, "data-testid": `privacy-top-stop-${i}`, children: [_jsxs("span", { style: {
                                                display: "inline-flex",
                                                alignItems: "baseline",
                                                gap: 8,
                                                minWidth: 0,
                                            }, children: [_jsx("span", { style: {
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: "var(--color-cool-ash)",
                                                        letterSpacing: "0.1em",
                                                    }, children: `${i + 1}.` }), _jsx("span", { style: {
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }, children: s.label })] }), _jsxs("span", { style: {
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: "var(--color-cool-ash)",
                                                letterSpacing: "0.1em",
                                            }, children: [s.count, " pulses"] })] }, s.stopId))) }))] })] })] }));
}
